<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div ref="root" class="anna-select">
    <oxd-text tag="p" class="anna-select-label">{{ label }}</oxd-text>
    <button
      type="button"
      class="anna-select-field"
      :class="{'--open': open}"
      :disabled="disabled"
      :aria-expanded="open"
      @click="toggle"
    >
      <span v-if="selected" class="anna-select-value">
        <span class="anna-select-name">{{ selected.label }}</span>
        <span v-if="selected.id !== selected.label" class="anna-select-sub">
          {{ selected.id }}
        </span>
      </span>
      <span v-else class="anna-select-placeholder">{{ placeholder }}</span>
      <i
        :class="['bi', open ? 'bi-caret-up-fill' : 'bi-caret-down-fill']"
        class="anna-select-caret"
        aria-hidden="true"
      />
    </button>

    <div v-if="open" class="anna-select-panel">
      <input
        ref="search"
        v-model="query"
        class="anna-select-search"
        type="text"
        placeholder="Search by name or id…"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="choose(items[highlight])"
        @keydown.esc="close"
      />
      <ul ref="list" class="anna-select-list" role="listbox">
        <li
          v-for="(option, i) in items"
          :key="option.id"
          role="option"
          :aria-selected="option.id === modelValue"
          :class="{
            '--active': i === highlight,
            '--selected': option.id === modelValue,
          }"
          @mousedown.prevent="choose(option)"
          @mouseenter="highlight = i"
        >
          <span class="anna-select-name">{{ option.label }}</span>
          <span v-if="option.id !== option.label" class="anna-select-sub">
            {{ option.id }}
          </span>
        </li>
        <li v-if="!items.length" class="anna-select-empty">No matches</li>
      </ul>
      <p class="anna-select-count">
        {{ filtered.length }} of {{ options.length }}
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchSelect',
  props: {
    label: {type: String, required: true},
    // [{id, label}]
    options: {type: Array, required: true},
    modelValue: {type: String, default: ''},
    placeholder: {type: String, default: 'Select…'},
    disabled: {type: Boolean, default: false},
    // Also accept a typed value that isn't in `options` (offered as "Use …").
    allowCustom: {type: Boolean, default: false},
  },
  emits: ['update:modelValue'],
  data() {
    return {open: false, query: '', highlight: 0};
  },
  computed: {
    selected() {
      if (!this.modelValue) return null;
      return (
        this.options.find((o) => o.id === this.modelValue) || {
          id: this.modelValue,
          label: this.allowCustom ? this.modelValue : '(not in this list)',
        }
      );
    },
    items() {
      const typed = this.query.trim();
      if (
        !this.allowCustom ||
        !typed ||
        this.options.some((o) => o.id === typed)
      ) {
        return this.filtered;
      }
      return [{id: typed, label: `Use “${typed}”`}, ...this.filtered];
    },
    filtered() {
      const needle = this.query.trim().toLowerCase();
      if (!needle) return this.options;
      return this.options.filter(
        (o) =>
          o.label.toLowerCase().includes(needle) ||
          o.id.toLowerCase().includes(needle),
      );
    },
  },
  watch: {
    query() {
      this.highlight = 0;
    },
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onOutside);
  },
  methods: {
    toggle() {
      this.open ? this.close() : this.openPanel();
    },
    openPanel() {
      this.open = true;
      this.query = '';
      const current = this.options.findIndex((o) => o.id === this.modelValue);
      this.highlight = Math.max(current, 0);
      document.addEventListener('mousedown', this.onOutside);
      this.$nextTick(() => {
        this.$refs.search?.focus();
        this.scrollToHighlight();
      });
    },
    close() {
      this.open = false;
      document.removeEventListener('mousedown', this.onOutside);
    },
    onOutside(event) {
      if (!this.$refs.root?.contains(event.target)) this.close();
    },
    move(step) {
      const last = this.items.length - 1;
      this.highlight = Math.min(Math.max(this.highlight + step, 0), last);
      this.$nextTick(this.scrollToHighlight);
    },
    scrollToHighlight() {
      this.$refs.list?.children[this.highlight]?.scrollIntoView({
        block: 'nearest',
      });
    },
    choose(option) {
      if (!option) return;
      this.$emit('update:modelValue', option.id);
      this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-select {
  position: relative;
}
.anna-select-label {
  font-size: 12px;
  font-weight: 700;
  color: #6a7482;
  margin-bottom: 6px;
}
.anna-select-field {
  width: 100%;
  min-height: 45px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 12px;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  &.--open,
  &:focus-visible {
    border-color: var(--oxd-primary-one-color);
    outline: none;
  }
  &:disabled {
    cursor: default;
    background: #f7f8fa;
  }
  .anna-select-caret {
    flex-shrink: 0;
    color: #9aa4b1;
    font-size: 12px;
  }
}
.anna-select-value {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
  overflow: hidden;
}
.anna-select-name {
  font-size: 13px;
  font-weight: 600;
  color: #16202b;
  white-space: nowrap;
}
.anna-select-sub {
  font-size: 11px;
  color: #9aa4b1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.anna-select-placeholder {
  font-size: 13px;
  color: #9aa4b1;
}
.anna-select-panel {
  position: absolute;
  z-index: 50;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e7ebf1;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(18, 35, 63, 0.16);
  padding: 8px;
}
.anna-select-search {
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: var(--oxd-primary-one-color);
  }
}
.anna-select-list {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  max-height: 280px;
  overflow-y: auto;
  li {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 10px;
    border-radius: 6px;
    cursor: pointer;
    &.--active {
      background: color-mix(in srgb, var(--oxd-primary-one-color) 8%, #fff);
    }
    &.--selected .anna-select-name {
      color: var(--oxd-primary-one-color);
    }
  }
  .anna-select-empty {
    cursor: default;
    color: #9aa4b1;
    font-size: 13px;
  }
}
.anna-select-count {
  margin: 6px 4px 0;
  font-size: 11px;
  color: #9aa4b1;
  text-align: right;
}
</style>
